import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Activity, AlertOctagon, BarChart3, Building2, Calendar, CheckCircle2, Clock,
  Coins, FileBarChart, Layers, LineChart as LineChartIcon, Medal, Package,
  PieChart as PieChartIcon, Save, Target, TrendingUp, Trophy, Users, Wallet, XCircle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, AreaChart, Area,
} from "recharts";
import { toast } from "sonner";
import { CHART_COLOR, WERTIA_CHART_PALETTE } from "@/lib/chartPalette";
import { fmtMoney as _fmtMoney, fmtInt as _fmtInt } from "@/lib/format";

function chf(n) {
  return `CHF ${_fmtInt(n)}`;
}
function chf2(n) {
  return `CHF ${_fmtMoney(n)}`;
}
// Palette grafici Wertia: tonalità brand + 3 semantici (cocoa/stone/gold/gold-dark/sand + info)
const CHART_COLORS = [...WERTIA_CHART_PALETTE, CHART_COLOR.INFO, CHART_COLOR.SUCCESS];

function KpiCard({ icon: Icon, label, value, sub, accent = "default", testId }) {
  const accents = {
    default: "border-border",
    gold: "border-prime/40 bg-prime/5",
    amber: "border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/15",
    emerald: "border-emerald-500/50 bg-emerald-50/40 dark:bg-emerald-950/15",
    blue: "border-blue-500/50 bg-blue-50/40 dark:bg-blue-950/15",
    red: "border-red-500/50 bg-red-50/40 dark:bg-red-950/15",
    violet: "border-violet-500/50 bg-violet-50/40 dark:bg-violet-950/15",
  };
  return (
    <div className={`prime-card p-4 border-2 ${accents[accent]}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="label-uppercase text-muted-foreground">{label}</div>
          <div className="font-display font-extrabold text-2xl lg:text-3xl mt-1 leading-none truncate">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
        </div>
        {Icon && <div className="opacity-30"><Icon className="w-7 h-7" strokeWidth={1.5} /></div>}
      </div>
    </div>
  );
}

function ProgressBar({ value, target, color = "bg-emerald-500" }) {
  const pct = Math.min(100, target > 0 ? (value / target) * 100 : 0);
  return (
    <div className="w-full h-2 bg-secondary/50 rounded-full overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${pct}%`, transition: "width .4s" }} />
    </div>
  );
}

export default function SuperAdminDashboardPage() {
  const [tab, setTab] = useState("antraege");
  const [antragStats, setAntragStats] = useState(null);
  const [overdueList, setOverdueList] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("month");
  const [billingMetric, setBillingMetric] = useState("lordo"); // lordo | netto | wertia
  const [ranking, setRanking] = useState(null);
  const [rankingDim, setRankingDim] = useState("consultant"); // consultant | team | product | insurer
  const [rankingBy, setRankingBy] = useState("chf"); // chf | count
  const [rankingPeriod, setRankingPeriod] = useState("month");
  const [editingTargets, setEditingTargets] = useState(null);

  useEffect(() => {
    (async () => {
      const [as, ov, cs, oj] = await Promise.all([
        api.get("/super-admin/antrag-stats"),
        api.get("/super-admin/antrag-overdue"),
        api.get("/super-admin/commissions-summary"),
        api.get("/super-admin/objectives"),
      ]);
      setAntragStats(as.data); setOverdueList(ov.data.items || []);
      setCommissions(cs.data); setObjectives(oj.data);
    })();
  }, []);

  useEffect(() => {
    api.get(`/super-admin/billing-trend?period=${billingPeriod}&months_back=12`).then(r => setBilling(r.data));
  }, [billingPeriod]);

  useEffect(() => {
    api.get(`/super-admin/ranking?period=${rankingPeriod}&dim=${rankingDim}&by=${rankingBy}`).then(r => setRanking(r.data));
  }, [rankingDim, rankingBy, rankingPeriod]);

  const saveTargets = async () => {
    if (!editingTargets) return;
    try {
      await api.put("/super-admin/objectives", editingTargets);
      toast.success("Ziele aktualisiert");
      const oj = await api.get("/super-admin/objectives");
      setObjectives(oj.data);
      setEditingTargets(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const antragChartData = useMemo(() => {
    if (!antragStats) return [];
    return [
      { name: "In Bearbeitung", value: antragStats.in_bearbeitung, color: CHART_COLOR.GOLD },
      { name: "Genehmigt", value: antragStats.approved, color: CHART_COLOR.SUCCESS },
      { name: "Abgelehnt", value: antragStats.rejected, color: CHART_COLOR.ERROR },
    ];
  }, [antragStats]);

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ Super Admin · Wertia Finanz Übersicht</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">
            Cockpit
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
            Vollständiges Auge auf das Geschäft — Anträge, Provisionen, Wertia-Ziele, Fakturierung und Ranking.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-5">
        <TabsList className="bg-secondary p-1 h-auto flex flex-wrap gap-1">
          <TabsTrigger value="antraege" data-testid="sa-tab-antraege" className="aria-[selected=true]:bg-emerald-500 aria-[selected=true]:text-white gap-1.5">
            <FileBarChart className="w-3.5 h-3.5" /> Anträge / Policen
            {antragStats?.overdue_14d > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold animate-pulse">
                {antragStats.overdue_14d}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="commissions" data-testid="sa-tab-commissions" className="aria-[selected=true]:bg-emerald-500 aria-[selected=true]:text-white gap-1.5">
            <Coins className="w-3.5 h-3.5" /> Provisionen
          </TabsTrigger>
          <TabsTrigger value="objectives" data-testid="sa-tab-objectives" className="aria-[selected=true]:bg-emerald-500 aria-[selected=true]:text-white gap-1.5">
            <Target className="w-3.5 h-3.5" /> Ziele Wertia
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="sa-tab-billing" className="aria-[selected=true]:bg-emerald-500 aria-[selected=true]:text-white gap-1.5">
            <LineChartIcon className="w-3.5 h-3.5" /> Fakturierung
          </TabsTrigger>
          <TabsTrigger value="ranking" data-testid="sa-tab-ranking" className="aria-[selected=true]:bg-emerald-500 aria-[selected=true]:text-white gap-1.5">
            <Trophy className="w-3.5 h-3.5" /> Ranking
          </TabsTrigger>
        </TabsList>

        {/* ---------------- SECTION 1: ANTRÄGE / POLICEN ---------------- */}
        <TabsContent value="antraege" className="space-y-5 m-0">
          {antragStats?.overdue_14d > 0 && (
            <div className="prime-card p-4 border-2 border-red-500 bg-red-50/40 dark:bg-red-950/15 flex items-start gap-3" data-testid="sa-overdue-alert">
              <AlertOctagon className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-display font-bold text-lg text-red-700 dark:text-red-300">
                  {antragStats.overdue_14d} Antrag/Anträge über die 14-Tage-Frist hinaus!
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Diese Anträge brauchen eine Rückmeldung der Gesellschaft. Notifikationen wurden an Super Admin + Berater gesendet.
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KpiCard icon={Activity}      label="Gesamt"          value={antragStats?.total ?? 0}                          accent="default" testId="sa-kpi-antrag-total" />
            <KpiCard icon={Clock}         label="In Bearbeitung"  value={antragStats?.in_bearbeitung ?? 0}                  accent="amber"   testId="sa-kpi-antrag-pending" />
            <KpiCard icon={CheckCircle2}  label="Genehmigt"       value={antragStats?.approved ?? 0}                        accent="emerald" testId="sa-kpi-antrag-approved" />
            <KpiCard icon={XCircle}       label="Abgelehnt"       value={antragStats?.rejected ?? 0}                        accent="red"     testId="sa-kpi-antrag-rejected" />
            <KpiCard icon={AlertOctagon}  label=">14 Tage offen"  value={antragStats?.overdue_14d ?? 0} sub={`Frist überschritten · Genehmigungsrate ${antragStats?.approval_rate ?? 0}%`} accent="red" testId="sa-kpi-antrag-overdue" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Pie chart */}
            <div className="prime-card p-4 lg:col-span-1">
              <div className="flex items-center gap-2 mb-3"><PieChartIcon className="w-4 h-4 text-prime" /><h3 className="font-display font-bold">Status-Verteilung</h3></div>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={antragChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {antragChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* By consultant */}
            <div className="prime-card p-4 lg:col-span-2">
              <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-prime" /><h3 className="font-display font-bold">Anträge pro Berater</h3></div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={antragStats?.by_consultant || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(125,125,125,0.2)" />
                  <XAxis dataKey="full_name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="pending"  name="In Bearb."  fill={CHART_COLOR.GOLD} stackId="a" />
                  <Bar dataKey="approved" name="Genehmigt"  fill={CHART_COLOR.SUCCESS} stackId="a" />
                  <Bar dataKey="rejected" name="Abgelehnt"  fill={CHART_COLOR.ERROR} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Overdue list */}
          {overdueList.length > 0 && (
            <div className="prime-card overflow-hidden" data-testid="sa-overdue-list">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <AlertOctagon className="w-4 h-4 text-red-600" />
                <h3 className="font-display font-bold">SLA überschritten · brauchen Entscheidung der Gesellschaft</h3>
              </div>
              <div className="divide-y divide-border">
                {overdueList.map(o => (
                  <Link key={o.id} to={`/clients/${o.client_id}`} className="block px-4 py-3 hover:bg-secondary/30 transition" data-testid={`sa-overdue-row-${o.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-sm font-semibold">{o.client_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{o.company} · {o.insurance_type} · Berater: {o.owner_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-display font-bold text-red-600">{o.age_days} Tage</div>
                        <div className="text-[10px] text-muted-foreground">seit Antrag</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---------------- SECTION 2: COMMISSIONS ---------------- */}
        <TabsContent value="commissions" className="space-y-5 m-0">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={CheckCircle2} label="Bezahlt"           value={chf(commissions?.totals?.paid)}        accent="emerald" testId="sa-kpi-paid" />
            <KpiCard icon={Activity}     label="In Bearbeitung"    value={chf(commissions?.totals?.processing)}  accent="amber" testId="sa-kpi-processing" />
            <KpiCard icon={Clock}        label="Offen"             value={chf(commissions?.totals?.unpaid)}      accent="red" testId="sa-kpi-unpaid" />
            <KpiCard icon={Calendar}     label="Verschoben"        value={chf(commissions?.totals?.carried_over)} accent="violet" testId="sa-kpi-carried" />
          </div>

          {/* Per consultant */}
          <div className="prime-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Users className="w-4 h-4 text-prime" />
              <h3 className="font-display font-bold">Pro Berater</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sa-commissions-by-consultant">
                <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Berater</th>
                    <th className="text-left px-3 py-2">Schema</th>
                    <th className="text-right px-3 py-2">Lordo</th>
                    <th className="text-right px-3 py-2">Berater (Netto)</th>
                    <th className="text-right px-3 py-2">Wertia-Anteil</th>
                    <th className="text-right px-3 py-2">Bezahlt</th>
                    <th className="text-right px-3 py-2">Offen</th>
                  </tr>
                </thead>
                <tbody>
                  {(commissions?.by_consultant || []).map(c => (
                    <tr key={c.owner_id} className="border-t border-border hover:bg-secondary/20">
                      <td className="px-4 py-2 font-medium">{c.full_name}</td>
                      <td className="px-3 py-2"><span className="text-[10px] font-bold uppercase">{c.scheme}</span> · {c.consultant_pct}%</td>
                      <td className="px-3 py-2 text-right font-mono">{chf2(c.net_gross)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">{chf2(c.consultant_share)}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700 dark:text-amber-400">{chf2(c.wertia_share)}</td>
                      <td className="px-3 py-2 text-right font-mono">{chf2(c.paid)}</td>
                      <td className="px-3 py-2 text-right font-mono">{chf2(c.unpaid)}</td>
                    </tr>
                  ))}
                  {(commissions?.by_consultant || []).length === 0 && (
                    <tr><td colSpan="7" className="text-center py-6 text-muted-foreground">Keine Provisionen vorhanden</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per team */}
          {commissions?.by_team?.length > 0 && (
            <div className="prime-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2"><Users className="w-4 h-4 text-prime" /><h3 className="font-display font-bold">Pro Team</h3></div>
              <div className="divide-y divide-border">
                {commissions.by_team.map(t => (
                  <div key={t.team_leader_id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{t.team_leader_name}</div>
                      <div className="text-xs text-muted-foreground">{t.members_count} Mitglied(er)</div>
                    </div>
                    <div className="text-right font-mono text-lg font-bold">{chf2(t.net_total)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---------------- SECTION 3: WERTIA OBJECTIVES ---------------- */}
        <TabsContent value="objectives" className="space-y-5 m-0">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Ziele für Monat & Jahr — Live-Vergleich mit Ist-Werten</p>
            {!editingTargets ? (
              <Button variant="outline" onClick={() => setEditingTargets({ ...objectives?.targets })} data-testid="sa-edit-targets">
                Ziele bearbeiten
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setEditingTargets(null)}>Abbrechen</Button>
                <Button className="btn-prime gap-1" onClick={saveTargets} data-testid="sa-save-targets"><Save className="w-3.5 h-3.5" /> Speichern</Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* CHF Mini Chart */}
            <ObjectiveCard
              title="Umsatz (CHF)"
              icon={Coins}
              targetKey="monthly_chf_target"
              targetKey2="yearly_chf_target"
              actualMonth={objectives?.actuals?.month_chf_gross}
              actualYear={objectives?.actuals?.year_chf_gross}
              netActualMonth={objectives?.actuals?.month_chf_net}
              wertiaActualMonth={objectives?.actuals?.month_chf_wertia}
              objectives={objectives}
              editingTargets={editingTargets}
              setEditingTargets={setEditingTargets}
              formatter={chf}
              accentColor="bg-prime"
            />
            <ObjectiveCard
              title="Anzahl Policen"
              icon={Layers}
              targetKey="monthly_policies_target"
              targetKey2="yearly_policies_target"
              actualMonth={objectives?.actuals?.month_policies}
              actualYear={objectives?.actuals?.year_policies}
              objectives={objectives}
              editingTargets={editingTargets}
              setEditingTargets={setEditingTargets}
              formatter={n => n}
              accentColor="bg-blue-500"
            />
            <ObjectiveCard
              title="Anzahl Kunden"
              icon={Users}
              targetKey="monthly_clients_target"
              targetKey2="yearly_clients_target"
              actualMonth={objectives?.actuals?.month_clients}
              actualYear={objectives?.actuals?.year_clients}
              objectives={objectives}
              editingTargets={editingTargets}
              setEditingTargets={setEditingTargets}
              formatter={n => n}
              accentColor="bg-violet-500"
            />
          </div>
        </TabsContent>

        {/* ---------------- SECTION 4: BILLING ---------------- */}
        <TabsContent value="billing" className="space-y-5 m-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 bg-secondary/40 rounded-md p-1">
              {["day", "month", "year"].map(p => (
                <button key={p} onClick={() => setBillingPeriod(p)}
                        className={`px-3 py-1.5 rounded text-xs font-semibold ${billingPeriod === p ? "bg-emerald-500 text-white" : "text-muted-foreground"}`}
                        data-testid={`sa-billing-period-${p}`}>
                  {p === "day" ? "Tag" : p === "month" ? "Monat" : "Jahr"}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-secondary/40 rounded-md p-1">
              {[
                { k: "lordo",  l: "Brutto" },
                { k: "netto",  l: "Berater (Netto)" },
                { k: "wertia", l: "Wertia-Anteil" },
              ].map(m => (
                <button key={m.k} onClick={() => setBillingMetric(m.k)}
                        className={`px-3 py-1.5 rounded text-xs font-semibold ${billingMetric === m.k ? "bg-emerald-500 text-white" : "text-muted-foreground"}`}
                        data-testid={`sa-billing-metric-${m.k}`}>
                  {m.l}
                </button>
              ))}
            </div>
          </div>

          <div className="prime-card p-4">
            <div className="flex items-center gap-2 mb-3"><LineChartIcon className="w-4 h-4 text-prime" /><h3 className="font-display font-bold">Fakturierung {billingPeriod === "day" ? "tageweise" : billingPeriod === "month" ? "monatlich" : "jährlich"}</h3></div>
            <ResponsiveContainer width="100%" height={360}>
              <AreaChart data={billing?.points || []}>
                <defs>
                  <linearGradient id="gradMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLOR.GOLD} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={CHART_COLOR.GOLD} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(125,125,125,0.2)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => chf2(v)} />
                <Area type="monotone" dataKey={billingMetric} stroke={CHART_COLOR.GOLD_DARK} strokeWidth={2} fill="url(#gradMain)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Comparison chart (all 3 metrics) */}
          <div className="prime-card p-4">
            <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-prime" /><h3 className="font-display font-bold">Vergleich aller Metriken</h3></div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={billing?.points || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(125,125,125,0.2)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => chf2(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="lordo"  name="Brutto"   fill={CHART_COLOR.COCOA} />
                <Bar dataKey="netto"  name="Berater"  fill={CHART_COLOR.SUCCESS} />
                <Bar dataKey="wertia" name="Wertia"   fill={CHART_COLOR.GOLD} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        {/* ---------------- SECTION 5: RANKING ---------------- */}
        <TabsContent value="ranking" className="space-y-5 m-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 bg-secondary/40 rounded-md p-1">
              {[
                { k: "consultant", l: "Berater", icon: Users },
                { k: "team",       l: "Team",    icon: Users },
                { k: "product",    l: "Produkt", icon: Package },
                { k: "insurer",    l: "Gesellschaft", icon: Building2 },
              ].map(d => (
                <button key={d.k} onClick={() => setRankingDim(d.k)}
                        className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1 ${rankingDim === d.k ? "bg-emerald-500 text-white" : "text-muted-foreground"}`}
                        data-testid={`sa-rank-dim-${d.k}`}>
                  <d.icon className="w-3 h-3" /> {d.l}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-secondary/40 rounded-md p-1">
              {[
                { k: "chf",   l: "CHF" },
                { k: "count", l: "Anzahl" },
              ].map(m => (
                <button key={m.k} onClick={() => setRankingBy(m.k)}
                        className={`px-3 py-1.5 rounded text-xs font-semibold ${rankingBy === m.k ? "bg-emerald-500 text-white" : "text-muted-foreground"}`}
                        data-testid={`sa-rank-by-${m.k}`}>
                  {m.l}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-secondary/40 rounded-md p-1">
              {[
                { k: "month", l: "Monat" },
                { k: "year",  l: "Jahr" },
                { k: "all",   l: "Alle" },
              ].map(p => (
                <button key={p.k} onClick={() => setRankingPeriod(p.k)}
                        className={`px-3 py-1.5 rounded text-xs font-semibold ${rankingPeriod === p.k ? "bg-emerald-500 text-white" : "text-muted-foreground"}`}
                        data-testid={`sa-rank-period-${p.k}`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>

          {/* Top 3 medals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map(i => {
              const row = (ranking?.rows || [])[i];
              const medals = [
                { color: "bg-gradient-to-br from-amber-400 to-yellow-600 text-white", icon: Trophy,  label: "1. Platz", score: row?.[rankingBy === "chf" ? "chf_gross" : "count"] },
                { color: "bg-gradient-to-br from-slate-300 to-slate-500 text-white",  icon: Medal,   label: "2. Platz", score: row?.[rankingBy === "chf" ? "chf_gross" : "count"] },
                { color: "bg-gradient-to-br from-orange-400 to-orange-700 text-white", icon: Medal,   label: "3. Platz", score: row?.[rankingBy === "chf" ? "chf_gross" : "count"] },
              ];
              const m = medals[i];
              const Icon = m.icon;
              return (
                <div key={i} className={`prime-card p-5 relative overflow-hidden ${!row ? "opacity-40" : ""}`} data-testid={`sa-rank-pos-${i+1}`}>
                  <div className={`absolute -top-4 -right-4 w-20 h-20 rounded-full ${m.color} opacity-25`} />
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${m.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="label-uppercase mt-3">{m.label}</div>
                  <div className="font-display font-extrabold text-xl mt-1 truncate">{row?.label || "—"}</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground uppercase">{rankingBy === "chf" ? "Brutto" : "Anzahl"}</div>
                      <div className="font-mono font-bold">{rankingBy === "chf" ? chf2(row?.chf_gross) : (row?.count ?? "—")}</div>
                    </div>
                    {rankingBy === "chf" && (
                      <div>
                        <div className="text-muted-foreground uppercase">Netto</div>
                        <div className="font-mono">{chf2(row?.chf_netto)}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Full ranking chart */}
          <div className="prime-card p-4">
            <div className="flex items-center gap-2 mb-3"><BarChart3 className="w-4 h-4 text-prime" /><h3 className="font-display font-bold">Vollständige Rangliste</h3></div>
            <ResponsiveContainer width="100%" height={Math.max(280, (ranking?.rows?.length || 0) * 36 + 40)}>
              <BarChart data={ranking?.rows || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(125,125,125,0.2)" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => rankingBy === "chf" ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} />
                <Tooltip formatter={v => rankingBy === "chf" ? chf2(v) : v} />
                <Bar dataKey={rankingBy === "chf" ? "chf_gross" : "count"} fill={CHART_COLOR.GOLD_DARK}>
                  {(ranking?.rows || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


function ObjectiveCard({
  title, icon: Icon, targetKey, targetKey2,
  actualMonth, actualYear,
  netActualMonth, wertiaActualMonth,
  objectives, editingTargets, setEditingTargets,
  formatter, accentColor,
}) {
  const monthTarget = objectives?.targets?.[targetKey] || 0;
  const yearTarget = objectives?.targets?.[targetKey2] || 0;
  return (
    <div className="prime-card p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className={`w-9 h-9 rounded-md ${accentColor} text-white flex items-center justify-center`}>
          {Icon && <Icon className="w-4 h-4" />}
        </div>
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>

      <div>
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Diesen Monat</span>
          <span className="font-mono text-xs">
            {formatter(actualMonth || 0)} / {editingTargets ? (
              <input
                type="number"
                value={editingTargets[targetKey] ?? monthTarget}
                onChange={e => setEditingTargets({ ...editingTargets, [targetKey]: parseFloat(e.target.value) })}
                className="w-20 inline bg-secondary px-1 rounded text-right"
                data-testid={`sa-target-${targetKey}`}
              />
            ) : formatter(monthTarget)}
          </span>
        </div>
        <ProgressBar value={actualMonth || 0} target={monthTarget} color={accentColor} />
        <div className="text-[10px] text-muted-foreground mt-1">
          {monthTarget > 0 ? `${Math.min(999, Math.round(((actualMonth || 0) / monthTarget) * 100))}% erreicht` : "Kein Ziel gesetzt"}
        </div>
      </div>

      <div>
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Dieses Jahr</span>
          <span className="font-mono text-xs">
            {formatter(actualYear || 0)} / {editingTargets ? (
              <input
                type="number"
                value={editingTargets[targetKey2] ?? yearTarget}
                onChange={e => setEditingTargets({ ...editingTargets, [targetKey2]: parseFloat(e.target.value) })}
                className="w-24 inline bg-secondary px-1 rounded text-right"
                data-testid={`sa-target-${targetKey2}`}
              />
            ) : formatter(yearTarget)}
          </span>
        </div>
        <ProgressBar value={actualYear || 0} target={yearTarget} color={accentColor} />
        <div className="text-[10px] text-muted-foreground mt-1">
          {yearTarget > 0 ? `${Math.min(999, Math.round(((actualYear || 0) / yearTarget) * 100))}% erreicht` : "Kein Ziel gesetzt"}
        </div>
      </div>

      {netActualMonth !== undefined && (
        <div className="grid grid-cols-2 gap-2 text-[11px] pt-2 border-t border-border">
          <div>
            <div className="text-muted-foreground uppercase">Berater (Netto)</div>
            <div className="font-mono font-bold text-emerald-700 dark:text-emerald-400">{formatter(netActualMonth)}</div>
          </div>
          <div>
            <div className="text-muted-foreground uppercase">Wertia-Anteil</div>
            <div className="font-mono font-bold text-amber-700 dark:text-amber-400">{formatter(wertiaActualMonth)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

